import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadPlatformChatUnions,
  loadPlatformUnionChatMessages,
  moderatePlatformUnionChatMessage,
  purgePlatformUnionChatSeason,
} from '../api/platformChatModerationApi.js';

function preferredSeason(union) {
  return union?.seasons?.find((season) => season.status === 'active') || union?.seasons?.[0] || null;
}

export function usePlatformChatModeration() {
  const [unions, setUnions] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [timeline, setTimeline] = useState({ union: null, season: null, messages: [] });
  const [loadingUnions, setLoadingUnions] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [workingMessageId, setWorkingMessageId] = useState('');
  const [purgingSeason, setPurgingSeason] = useState(false);
  const [notice, setNotice] = useState({ type: '', message: '' });

  const selectedUnion = useMemo(
    () => unions.find((union) => union.unionId === selectedUnionId) || null,
    [selectedUnionId, unions],
  );
  const selectedSeason = useMemo(
    () => selectedUnion?.seasons?.find((season) => season.seasonId === selectedSeasonId) || null,
    [selectedSeasonId, selectedUnion],
  );

  const refreshUnions = useCallback(async ({ preserveSelection = true } = {}) => {
    setLoadingUnions(true);
    try {
      const nextUnions = await loadPlatformChatUnions();
      setUnions(nextUnions);
      const currentUnion = preserveSelection
        ? nextUnions.find((union) => union.unionId === selectedUnionId)
        : null;
      const nextUnion = currentUnion || nextUnions[0] || null;
      const currentSeason = nextUnion?.seasons?.find((season) => season.seasonId === selectedSeasonId) || null;
      const nextSeason = currentSeason || preferredSeason(nextUnion);
      setSelectedUnionId(nextUnion?.unionId || '');
      setSelectedSeasonId(nextSeason?.seasonId || '');
      if (!nextUnion) setTimeline({ union: null, season: null, messages: [] });
      setNotice((current) => (current.type === 'success' ? current : { type: '', message: '' }));
      return nextUnions;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '검수 가능한 유니온 목록을 불러오지 못했습니다.',
      });
      return [];
    } finally {
      setLoadingUnions(false);
    }
  }, [selectedSeasonId, selectedUnionId]);

  const refreshMessages = useCallback(async ({
    unionId = selectedUnionId,
    seasonId = selectedSeasonId,
    union = selectedUnion,
    season = selectedSeason,
  } = {}) => {
    if (!unionId) {
      setTimeline({ union: null, season: null, messages: [] });
      return null;
    }
    setLoadingMessages(true);
    try {
      const nextTimeline = await loadPlatformUnionChatMessages({
        unionId,
        seasonId,
        union,
        season,
        includeHidden: true,
        limit: 300,
      });
      setTimeline(nextTimeline);
      setNotice((current) => (current.type === 'success' ? current : { type: '', message: '' }));
      return nextTimeline;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '유니온 채팅 내역을 불러오지 못했습니다.',
      });
      return null;
    } finally {
      setLoadingMessages(false);
    }
  }, [selectedSeason, selectedSeasonId, selectedUnion, selectedUnionId]);

  useEffect(() => {
    void refreshUnions({ preserveSelection: false });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUnionId) return;
    void refreshMessages({ unionId: selectedUnionId, seasonId: selectedSeasonId });
  }, [refreshMessages, selectedSeasonId, selectedUnionId]);

  const selectUnion = useCallback((unionId) => {
    const union = unions.find((item) => item.unionId === unionId) || null;
    const season = preferredSeason(union);
    setSelectedUnionId(union?.unionId || '');
    setSelectedSeasonId(season?.seasonId || '');
    setNotice({ type: '', message: '' });
  }, [unions]);

  const selectSeason = useCallback((seasonId) => {
    setSelectedSeasonId(String(seasonId || ''));
    setNotice({ type: '', message: '' });
  }, []);

  const moderate = useCallback(async ({ messageId, action, reason }) => {
    if (!messageId || workingMessageId || !selectedUnionId || !selectedSeasonId) return false;
    setWorkingMessageId(messageId);
    setNotice({ type: '', message: '' });
    try {
      await moderatePlatformUnionChatMessage({
        unionId: selectedUnionId,
        seasonId: selectedSeasonId,
        messageId,
        action,
        reason,
      });
      await Promise.all([
        refreshMessages({ unionId: selectedUnionId, seasonId: selectedSeasonId }),
        refreshUnions({ preserveSelection: true }),
      ]);
      setNotice({
        type: 'success',
        message: action === 'restore'
          ? '메시지를 복원했습니다.'
          : action === 'purge'
            ? '메시지를 영구 삭제했습니다.'
            : '메시지를 유니온 채팅에서 숨겼습니다.',
      });
      return true;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '메시지 관리 요청을 처리하지 못했습니다.',
      });
      return false;
    } finally {
      setWorkingMessageId('');
    }
  }, [refreshMessages, refreshUnions, selectedSeasonId, selectedUnionId, workingMessageId]);

  const purgeSeason = useCallback(async (reason) => {
    if (purgingSeason || !selectedUnionId || !selectedSeasonId) return false;
    setPurgingSeason(true);
    setNotice({ type: '', message: '' });
    try {
      const result = await purgePlatformUnionChatSeason({
        unionId: selectedUnionId,
        seasonId: selectedSeasonId,
        reason,
      });
      await Promise.all([
        refreshMessages({ unionId: selectedUnionId, seasonId: selectedSeasonId }),
        refreshUnions({ preserveSelection: true }),
      ]);
      setNotice({
        type: 'success',
        message: `${selectedSeason?.seasonNo ? `${selectedSeason.seasonNo}차 ` : ''}채팅 ${result.purgedCount}건을 영구 삭제했습니다.`,
      });
      return true;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '차수 채팅 전체 삭제에 실패했습니다.',
      });
      return false;
    } finally {
      setPurgingSeason(false);
    }
  }, [purgingSeason, refreshMessages, refreshUnions, selectedSeason, selectedSeasonId, selectedUnionId]);

  return {
    unions,
    selectedUnion,
    selectedSeason,
    selectedUnionId,
    selectedSeasonId,
    timeline,
    loadingUnions,
    loadingMessages,
    workingMessageId,
    purgingSeason,
    notice,
    selectUnion,
    purgeSeason,
    selectSeason,
    refreshUnions,
    refreshMessages,
    moderate,
  };
}
